import { ReactNode } from 'react';

import useAuthContext from 'hooks/useAuthContext';

type UserRole = 'ADMIN' | 'INSTRUCTOR' | 'STUDENT';

interface RequireRoleProps {
  /**
   * Single role or array of roles that are allowed to view the content.
   * If an array is provided, the user must have at least one of the roles.
   */
  role: UserRole | UserRole[];

  /**
   * Content to render if the user has the required role(s).
   */
  children: ReactNode;

  /**
   * Optional fallback content to render if the user doesn't have the required role(s).
   * If not provided, nothing will be rendered when access is denied.
   */
  fallback?: ReactNode;
}

/**
 * Component that conditionally renders content based on the user's role.
 *
 * @example
 * // Single role check
 * <RequireRole role="ADMIN">
 *   <AdminPanel />
 * </RequireRole>
 *
 * @example
 * // Multiple roles check (user needs at least one)
 * <RequireRole role={['ADMIN', 'INSTRUCTOR']}>
 *   <ManagementDashboard />
 * </RequireRole>
 *
 * @example
 * // With fallback content
 * <RequireRole role="ADMIN" fallback={<p>Access denied</p>}>
 *   <AdminPanel />
 * </RequireRole>
 */
export function RequireRole({ role, children, fallback = null }: RequireRoleProps) {
  const { userRole } = useAuthContext();

  // Check if user has the required role
  const hasRequiredRole = Array.isArray(role) ? role.includes(userRole!) : userRole === role;

  if (hasRequiredRole) {
    return children;
  }

  return fallback;
}

export default RequireRole;
